import { useCallback, useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  KiteButton,
  KiteIcon,
  KiteTag,
  KiteMenu,
  KiteMenuItem,
  KiteProgressIndicator,
} from '@kite/react-kite';

import useGeneratePdfBase64 from 'hooks/apiCalls/useGeneratePdfBase64';
import useComparePdf from 'hooks/apiCalls/useComparePdf';
import useFetchOrderTypeChangelog from 'hooks/apiCalls/useFetchOrderTypeChangelog';
import SideBySideComparison from 'components/SideBySideComparison';

import './PreviewPDF.scss';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

interface PreviewPDFProps {
  pdfPayload?: {
    pickedPlatform: string;
    orderType?: string;
    customerSegment?: string;
    servicesRepresented?: string;
    firstName?: string;
    accountNumber?: string;
    facility?: string;
    internetOrder?: string;
    wifiOrder?: string;
    voiceOrder?: string;
    videoOrder?: string;
    versionRequested?: string;
  };
  pickedPlatform?: string;
  orderType?: string;
  collateralId?: string;
  currentVersion?: string;
  status?: string;
  onError?: (error: string) => void;
}

/** Format any raw version value into a "VERSION X.X" label. */
const formatVersionLabel = (raw?: string): string => {
  const v = raw?.toString().trim();
  if (!v) return '';
  return /^version/i.test(v) ? v.toUpperCase() : `VERSION ${v}`;
};

const PreviewPDF = ({
  pdfPayload,
  pickedPlatform,
  orderType,
  collateralId,
  currentVersion,
  status,
  onError,
}: PreviewPDFProps) => {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [documentLoadFailed, setDocumentLoadFailed] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [sideBySideComparison, setSideBySideComparison] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [compareVersion, setCompareVersion] = useState('');
  const [sourcePageNumber, setSourcePageNumber] = useState(1);
  const [targetPageNumber, setTargetPageNumber] = useState(1);
  const [sourceNumPages, setSourceNumPages] = useState<number>();
  const [targetNumPages, setTargetNumPages] = useState<number>();

  // Version list comes from the changelog endpoint; the current version is
  // always included and used as the default selection.
  const { data: changelogData } = useFetchOrderTypeChangelog(collateralId);

  const availableVersions = useMemo<string[]>(() => {
    const fromLogs = (changelogData?.changeLogs ?? [])
      .map((log) => log.version?.toString().trim())
      .filter((v): v is string => Boolean(v));
    const current = currentVersion?.toString().trim();
    const all = current ? [current, ...fromLogs] : fromLogs;
    return Array.from(new Set(all));
  }, [changelogData?.changeLogs, currentVersion]);

  /**
   * Default version, derived synchronously so the first PDF request already
   * carries a version. Deriving this in an effect instead would fire one
   * request without a version and a second one with it.
   */
  const defaultVersion = useMemo<string>(
    () => currentVersion?.toString().trim() || availableVersions[0] || '',
    [currentVersion, availableVersions]
  );

  /** User selection wins; otherwise fall back to the derived default. */
  const effectiveVersion = selectedVersion || defaultVersion;

  // Default compare version to the second available version (previous).
  useEffect(() => {
    if (availableVersions.length > 1) {
      setCompareVersion(availableVersions[1]);
    }
  }, [availableVersions]);

  const {
    generatePdf,
    pdfResponse: data,
    isLoading,
    isError,
    error,
  } = useGeneratePdfBase64();

  const {
    comparePdf,
    compareResponse,
    isLoading: isComparingPdf,
    isError: isCompareError,
    error: compareError,
  } = useComparePdf();

  const isValidPickedPlatform = (value?: string): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const pickedPlatformValue =
    pdfPayload?.pickedPlatform || pickedPlatform || '';
  const orderTypeValue = pdfPayload?.orderType || orderType || '';
  const hasValidPayload =
    !!pdfPayload && isValidPickedPlatform(pdfPayload.pickedPlatform);
  const hasValidPickedPlatform = isValidPickedPlatform(pickedPlatformValue);

  /**
   * Build the generate-PDF request payload. The selected version is sent as
   * `versionRequested` so the backend can target that collateral version;
   * omitted when no version is selected so the latest is returned.
   */
  const buildGeneratePayload = useCallback(() => {
    const base =
      hasValidPayload && pdfPayload
        ? { ...pdfPayload }
        : { pickedPlatform: pickedPlatformValue };

    return {
      ...base,
      ...(orderTypeValue && { orderType: orderTypeValue }),
      ...(effectiveVersion && { versionRequested: effectiveVersion }),
    };
  }, [
    hasValidPayload,
    pdfPayload,
    pickedPlatformValue,
    orderTypeValue,
    effectiveVersion,
  ]);

  useEffect(() => {
    if (!hasValidPayload && !hasValidPickedPlatform) return;
    generatePdf(buildGeneratePayload());
  }, [
    hasValidPayload,
    hasValidPickedPlatform,
    buildGeneratePayload,
    generatePdf,
  ]);

  useEffect(() => {
    if (isError && onError) {
      onError('Failed to generate PDF. Please try again.');
    }
  }, [isError, error, onError]);

  useEffect(() => {
    if (isCompareError && onError) {
      onError('Failed to compare PDFs. Please try again.');
    }
  }, [isCompareError, compareError, onError]);

  const handleSideBySideToggle = async (checked: boolean) => {
    setSideBySideComparison(checked);

    if (checked && pickedPlatformValue) {
      // Compare current version against the selected compare version
      try {
        await comparePdf({
          pickedPlatform: pickedPlatformValue,
          orderType: orderTypeValue,
          sourceVersion: effectiveVersion,
          targetVersion: compareVersion,
        });
      } catch (err) {
        console.error('Failed to compare PDFs:', err);
        setSideBySideComparison(false);
      }
    }
  };

  const handleDownload = () => {
    if (!data?.pdf) return;
    const link = document.createElement('a');
    link.href = data.pdf;
    link.download = `${collateralId || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (!data?.pdf) return;
    const base64 = data.pdf.replace(/^data:application\/pdf;base64,/, '');
    const binary = window.atob(base64);
    const byteArray = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) byteArray[i] = binary.charCodeAt(i);
    const blobUrl = URL.createObjectURL(
      new Blob([byteArray], { type: 'application/pdf' })
    );
    const printWindow = window.open(blobUrl, '_blank');
    printWindow?.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    });
  };

  const renderFloatingControls = () => {
    const isComparison =
      sideBySideComparison && (sourceNumPages || targetNumPages);
    const currentPage = isComparison ? sourcePageNumber : pageNumber;
    const totalPages = isComparison ? sourceNumPages || 1 : numPages || 1;

    const handlePreviousPage = () => {
      if (isComparison) {
        setSourcePageNumber((p) => Math.max(1, p - 1));
        setTargetPageNumber((p) => Math.max(1, p - 1));
      } else {
        setPageNumber((p) => Math.max(1, p - 1));
      }
    };

    const handleNextPage = () => {
      if (isComparison) {
        setSourcePageNumber((p) => Math.min(sourceNumPages || p, p + 1));
        setTargetPageNumber((p) => Math.min(targetNumPages || p, p + 1));
      } else {
        setPageNumber((p) => Math.min(numPages || p, p + 1));
      }
    };

    const handleThumbnailClick = (page: number) => {
      if (isComparison) {
        setSourcePageNumber(page);
        setTargetPageNumber(page);
      } else {
        setPageNumber(page);
      }
    };

    return (
      <div className="floating-controls" data-testid="floating-controls">
        {/* Top box: page thumbnails + navigation arrows */}
        <div className="floating-box floating-box--pages">
          <div className="page-thumbnails-floating">
            {totalPages > 0 &&
              Array.from({ length: Math.min(totalPages, 2) }, (_, i) => (
                <div
                  key={i + 1}
                  className={`page-thumbnail ${
                    currentPage === i + 1 ? 'active' : ''
                  }`}
                  onClick={() => handleThumbnailClick(i + 1)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to page ${i + 1}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleThumbnailClick(i + 1);
                    }
                  }}
                >
                  <span className="page-number">{i + 1}</span>
                </div>
              ))}
          </div>

          {/* Navigation Arrows */}
          <div className="page-navigation">
            <KiteButton
              variant="borderless"
              size="shrinkwrap"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              aria-label="Previous Page"
              className="nav-btn"
            >
              <KiteIcon icon="ki-chevron-up" />
            </KiteButton>
            <KiteButton
              variant="borderless"
              size="shrinkwrap"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next Page"
              className="nav-btn"
            >
              <KiteIcon icon="ki-chevron-down" />
            </KiteButton>
          </div>
        </div>

        {/* Bottom box (detached): zoom controls */}
        <div className="floating-box floating-box--zoom">
          <div className="zoom-controls-floating">
            <KiteButton
              variant="borderless"
              size="shrinkwrap"
              onClick={() => setScale((s) => Math.min(3, s + 0.1))}
              disabled={scale >= 3}
              aria-label="Zoom In"
              className="zoom-btn-floating"
            >
              <KiteIcon icon="ki-zoom" />
            </KiteButton>
            <KiteButton
              variant="borderless"
              size="shrinkwrap"
              onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
              disabled={scale <= 0.5}
              aria-label="Zoom Out"
              className="zoom-btn-floating"
            >
              <KiteIcon icon="ki-zoom-out" />
            </KiteButton>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || isComparingPdf) {
    return (
      <div className="preview-container">
        <div className="preview-loading">
          <KiteProgressIndicator
            id="preview-pdf-loading"
            title="Loading"
            message={isComparingPdf ? 'Comparing PDFs...' : 'Loading PDF...'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="preview-container">
      {/* PDF Viewer and Side Controls */}
      <div className="pdf-viewer-container">
        <div className="pdf-main-area">
          {/* Version and More Options Header — hidden during side-by-side */}
          {!(sideBySideComparison && compareResponse) && (
          <div className="preview-header">
            <div className="version-info">
              <select
                className="version-select"
                value={effectiveVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                disabled={availableVersions.length <= 1}
              >
                {availableVersions.map((version) => (
                  <option key={version} value={version}>
                    {formatVersionLabel(version)}
                  </option>
                ))}
                {availableVersions.length === 0 && (
                  <option value="">VERSION -</option>
                )}
              </select>
              {status && (
                <KiteTag
                  icon="ki-checkmark-circle-f"
                  className="production-badge"
                >
                  {status}
                </KiteTag>
              )}
            </div>

            <KiteMenu
              ariaLabel="More Options"
              menuIcon="ki-menu-dots"
              className="more-options-menu"
            >
              <KiteMenuItem icon="ki-download" onClick={handleDownload}>
                Download
              </KiteMenuItem>
              <KiteMenuItem icon="ki-print" onClick={handlePrint}>
                Print
              </KiteMenuItem>
            </KiteMenu>
          </div>
          )}

          {/* PDF Document */}
          <div className="pdf-document-wrapper">
            {sideBySideComparison && compareResponse ? (
              <SideBySideComparison
                sourcePdfBase64={compareResponse.sourcePdfBase64}
                targetPdfBase64={compareResponse.targetPdfBase64}
                sourcePageNumber={sourcePageNumber}
                targetPageNumber={targetPageNumber}
                scale={scale}
                sourceVersion={effectiveVersion}
                targetVersion={compareVersion}
                availableVersions={availableVersions}
                onSourceVersionChange={(v) => {
                  setSelectedVersion(v);
                  if (pickedPlatformValue) {
                    comparePdf({
                      pickedPlatform: pickedPlatformValue,
                      orderType: orderTypeValue,
                      sourceVersion: v,
                      targetVersion: compareVersion,
                    });
                  }
                }}
                onTargetVersionChange={(v) => {
                  setCompareVersion(v);
                  if (pickedPlatformValue) {
                    comparePdf({
                      pickedPlatform: pickedPlatformValue,
                      orderType: orderTypeValue,
                      sourceVersion: effectiveVersion,
                      targetVersion: v,
                    });
                  }
                }}
                onSourceLoadSuccess={({ numPages }) => {
                  setSourceNumPages(numPages);
                  setSourcePageNumber(1);
                }}
                onTargetLoadSuccess={({ numPages }) => {
                  setTargetNumPages(numPages);
                  setTargetPageNumber(1);
                }}
              />
            ) : (
              <Document
                file={data?.pdf}
                onLoadSuccess={({ numPages }) => {
                  setNumPages(numPages);
                  setPageNumber(1);
                  setDocumentLoadFailed(false);
                }}
                onLoadError={() => setDocumentLoadFailed(true)}
                loading={
                  <div className="preview-loading" data-testid="pdf-loading">
                    <KiteProgressIndicator
                      id="preview-document-loading"
                      title="Loading"
                      message="Loading PDF, this may take a moment..."
                    />
                  </div>
                }
                error={
                  <div className="pdf-load-error" data-testid="pdf-load-error">
                    <KiteIcon icon="ki-caution-circle-f" />
                    <p>We couldn&apos;t load the PDF.</p>
                    <KiteButton
                      variant="secondary"
                      size="shrinkwrap"
                      onClick={() => {
                        setDocumentLoadFailed(false);
                        if (hasValidPayload || hasValidPickedPlatform) {
                          generatePdf(buildGeneratePayload());
                        }
                      }}
                    >
                      Retry
                    </KiteButton>
                  </div>
                }
                className="pdf-document"
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  className="pdf-page"
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            )}
          </div>

          {/* Floating Controls */}
          {!documentLoadFailed &&
            (numPages || sourceNumPages || targetNumPages) &&
            renderFloatingControls()}

          {/* Toggle Switches - Below PDF */}
          <div className="pdf-toggle-controls">
            <div className="toggle-item">
              <span className="toggle-label">Show Changes</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={showChanges}
                  onChange={(e) => setShowChanges(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="toggle-item">
              <span className="toggle-label">Show Side-By-Side Comparison</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={sideBySideComparison}
                  onChange={(e) => handleSideBySideToggle(e.target.checked)}
                  disabled={isComparingPdf || availableVersions.length < 1}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPDF;
